import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Briefcase, Building2, Users, HardHat, Award, GraduationCap, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import QstLayout, { AiSafetyBadge } from "./QstLayout";
import QstAssistantWidget from "./QstAssistantWidget";

const AUDIENCES = [
  { key: "trade-contractors",   Icon: HardHat,      title: "Trade Contractors",   desc: "Keep every crew and every project trained on the right lesson before work begins.", bullets: ["Multiple crews and supervisors","Recurring training + reminders","Worker acknowledgments and reports","Trade-specific subscription bundles"] },
  { key: "general-contractors", Icon: Briefcase,    title: "General Contractors", desc: "Standardize training across every subcontractor on every project.", bullets: ["Project & location dashboards","Contractor participation reports","Quality and loss-prevention programs","Organization-wide administration"] },
  { key: "project-owners",      Icon: Building2,    title: "Project Owners",      desc: "Verify contractor training participation and reduce claims frequency.", bullets: ["Exportable participation records","Real-time compliance visibility","Multi-project overview","Third-party verification"] },
  { key: "associations",        Icon: Users,        title: "Trade Associations",  desc: "Give every member the same expert training at a member-friendly price.", bullets: ["Multi-company licensing","Member portals","Co-branded programs","Aggregate reporting"] },
  { key: "insurers",            Icon: ShieldCheck,  title: "Insurers & Brokers",  desc: "Support policyholders with loss-prevention training tied to their trade.", bullets: ["Custom training collections","Bulk pricing","Loss-prevention dashboards","Renewal touch-point content"] },
  { key: "ocip-ccip",           Icon: Award,        title: "OCIP / CCIP Programs", desc: "Distribute the same expert-reviewed content across every contractor on your program.", bullets: ["Program-wide content distribution","Contractor onboarding","Participation reporting","Project & contractor comparisons"] },
  { key: "municipalities",      Icon: GraduationCap, title: "Municipalities & Mentorship", desc: "Support disadvantaged and small contractors with a structured training path.", bullets: ["Contractor-development programs","Structured training paths","Multilingual resources","Completion certificates & reports"] },
];

export default function QstForOrganizationsPage() {
  useEffect(() => { document.title = "For Organizations — QST"; }, []);
  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "for-orgs" }} />
      <section className="bg-[#0B2545] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">For Organizations</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">Training that scales with your program.</h1>
          <p className="mt-3 text-lg text-slate-200 max-w-3xl">
            One expert-authored library. Every contractor. Every trade. Every project. QST enterprise plans give
            administrators the control and reports they need — without giving up field usability.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/qst/contact?intent=enterprise"
              className="inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 font-bold shadow-md"
              data-testid="qst-fororg-cta">
              Request a Proposal <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/qst/contact?intent=demo"
              className="inline-flex items-center gap-2 rounded-md border-2 border-white/50 hover:bg-white/10 text-white px-6 py-3 font-bold">
              Book a Demonstration
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {AUDIENCES.map((a) => (
            <div key={a.key} className="rounded-xl border border-slate-200 bg-white p-6"
                 data-testid={`qst-org-audience-${a.key}`}>
              <div className="w-11 h-11 rounded-lg bg-[#F97316]/12 text-[#F97316] grid place-items-center">
                <a.Icon className="w-5.5 h-5.5" />
              </div>
              <div className="mt-4 text-lg font-bold text-[#0B2545]">{a.title}</div>
              <div className="mt-1 text-sm text-slate-600">{a.desc}</div>
              <ul className="mt-4 space-y-1.5">
                {a.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-800">
                    <CheckCircle2 className="w-4 h-4 text-[#059669] mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-14">
        <div className="max-w-5xl mx-auto px-6">
          <AiSafetyBadge>
            Administrators receive exportable participation reports at any scale — every training acknowledgment
            references an approved, versioned lesson.
          </AiSafetyBadge>
        </div>
      </section>
    </QstLayout>
  );
}

/* ============ About ============ */
export function QstAboutPage() {
  useEffect(() => { document.title = "About — Quality Safety Times"; }, []);
  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "home" }} />
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">About QST</div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black text-[#0B2545] tracking-tight">
          Construction expertise first. Technology that helps deliver it.
        </h1>
        <div className="mt-6 space-y-5 text-slate-700 leading-relaxed text-lg">
          <p>
            Quality Safety Times was founded by experienced construction professionals who watched the same
            preventable rework and injuries happen on every project. The industry's institutional knowledge
            was retiring faster than it could be captured.
          </p>
          <p>
            QST captures that knowledge, packages it as short, trade-specific lessons and puts it in front of
            the foreman before the crew begins work. Every lesson is authored by an experienced construction
            professional, reviewed by a technical peer, versioned, and dated.
          </p>
          <p>
            Our AI assistants search, summarize, translate and organize approved content — but they never
            invent safety requirements, regulatory citations, or replace the qualified judgment of the
            competent person on your project.
          </p>
        </div>
        <div className="mt-8"><AiSafetyBadge /></div>
      </section>
    </QstLayout>
  );
}

/* ============ Solutions category stub ============ */
export function QstSolutionsPage() {
  const { slug } = useParams();
  const label = slug ? slug.replace(/-/g, ' ') : 'Solutions';
  useEffect(() => { document.title = `${label} — QST`; }, [label]);
  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "home", toolLabel: "Solutions" }} />
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Solutions</div>
        <h1 className="mt-2 text-4xl font-black text-[#0B2545] tracking-tight capitalize">{label}</h1>
        <p className="mt-4 text-lg text-slate-700 max-w-2xl">
          Detailed solution page coming soon. Every solution builds on the same expert-authored QST library —
          the same lessons, targeted for a specific team need.
        </p>
        <Link to="/qst/library" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 font-bold">
          Explore the training library <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </QstLayout>
  );
}

/* ============ Resources index ============ */
export function QstResourcesPage() {
  useEffect(() => { document.title = "Resources — QST"; }, []);
  const cards = [
    { title: "Free Training Samples", to: "/qst/library?free_only=1", desc: "Try any of our free lessons — no account required." },
    { title: "Rework Cost Calculator", to: "/qst/tools/rework-cost", desc: "See the true cost of the last rework on your project." },
    { title: "Injury Cost Calculator", to: "/qst/tools/injury-cost", desc: "Direct + indirect cost of a workplace injury." },
    { title: "Tell Your Story",        to: "/qst/story/tell",        desc: "Contribute a lesson learned — reviewed by editors, never auto-published." },
    { title: "Frequently Asked Questions", to: "/qst/resources/faq", desc: "How QST works, what our AI does and does not do, subscription details." },
  ];
  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "home", toolLabel: "Resources" }} />
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Resources</div>
        <h1 className="mt-2 text-4xl font-black text-[#0B2545] tracking-tight">Everything you need — free and open.</h1>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {cards.map((c) => (
            <Link key={c.to} to={c.to}
              className="rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md transition">
              <div className="text-base font-bold text-[#0B2545]">{c.title}</div>
              <div className="mt-1 text-sm text-slate-600">{c.desc}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#F97316]">
                Open <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </QstLayout>
  );
}

/* ============ Generic tool placeholder ============ */
export function QstToolPlaceholderPage({ title, subtitle }) {
  useEffect(() => { document.title = `${title} — QST`; }, [title]);
  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "tools" }} />
      <section className="max-w-3xl mx-auto px-6 py-16 text-center">
        <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Tool</div>
        <h1 className="mt-2 text-4xl font-black text-[#0B2545] tracking-tight">{title}</h1>
        <p className="mt-3 text-slate-700">{subtitle}</p>
        <Link to="/qst/tools" className="mt-8 inline-flex items-center gap-2 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-800 px-5 py-2.5 font-bold">
          ← Back to Tools
        </Link>
      </section>
    </QstLayout>
  );
}

/* ============ Simple signup / signin stubs (Phase 2) ============ */
export function QstSignupPage() {
  useEffect(() => { document.title = "Start Free — QST"; }, []);
  return (
    <QstLayout>
      <section className="max-w-lg mx-auto px-6 py-16 text-center">
        <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Start Free</div>
        <h1 className="mt-2 text-4xl font-black text-[#0B2545]">Accounts open next week.</h1>
        <p className="mt-4 text-slate-700">
          Full account creation and Stripe billing arrive in Phase 2. In the meantime you can explore the
          full free library and calculators without an account.
        </p>
        <Link to="/qst/library?free_only=1" className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 font-bold">
          Explore free lessons <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </QstLayout>
  );
}
